export interface LetterSettings {
  margins?: { top?: number; right?: number; bottom?: number; left?: number };
  fontFamily?: string;
  fontSize?: number;
  headerHtml?: string;
  footerHtml?: string;
  signatureBlock?: string;
  useCompanyBranding?: boolean;
}

export interface LetterTemplate {
  id: string;
  title: string;
  description?: string;
  htmlContent: string;
  originalFileName?: string;
  originalFileType?: string;
  detectedVariables?: string[];
  category: string;
  isGlobal: boolean;
  isActive: boolean;
  usageCount: number;
  letterSettings?: LetterSettings;
  createdAt: string;
  updatedAt: string;
  creator?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

export interface PopulateResult {
  populatedHtml: string;
  filledVariables: Record<string, string>;
  remainingVariables: string[];
}

export interface LetterTemplateListResponse {
  templates: LetterTemplate[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface LetterTemplateSearchOptions {
  search?: string;
  category?: string;
  isActive?: boolean;
  limit?: number;
  offset?: number;
  sortBy?: 'title' | 'usageCount' | 'createdAt';
  sortOrder?: 'ASC' | 'DESC';
}

/** Standard variables that can be auto-populated from report data */
export const STANDARD_VARIABLES = [
  // Insurance-specific (commonly used in letters)
  { name: 'company_name', label: 'Company Name', description: 'Your agency/company name (auto-filled)' },
  { name: 'customer_name', label: 'Customer Name', description: 'Name of the insured/customer (auto-filled)' },
  { name: 'contact_name', label: 'Contact Name', description: 'Contact person at the customer' },
  { name: 'agent_name', label: 'Agent Name', description: 'Agent/broker name (auto-filled from your profile)' },
  { name: 'policy_number', label: 'Policy Number', description: 'Policy number' },
  { name: 'policy_type', label: 'Policy Type', description: 'Line of business or policy type' },
  { name: 'carrier_name', label: 'Carrier Name', description: 'Insurance carrier name' },
  { name: 'effective_date', label: 'Effective Date', description: 'Policy effective date' },
  { name: 'expiration_date', label: 'Expiration Date', description: 'Policy expiration date' },
  { name: 'premium', label: 'Premium', description: 'Premium amount' },
  // General / report-related (auto-filled)
  { name: 'date', label: 'Today\'s Date', description: 'Current date (auto-filled)' },
  { name: 'report_title', label: 'Report Title', description: 'Title of the workflow report (auto-filled)' },
  { name: 'report_date', label: 'Report Date', description: 'Date the report was created (auto-filled)' },
  { name: 'workflow_type', label: 'Workflow Type', description: 'Type of workflow analysis (auto-filled)' },
  { name: 'project_name', label: 'Project Name', description: 'Name of the project folder (auto-filled)' },
  { name: 'document_names', label: 'Document Names', description: 'Comma-separated list of analyzed documents (auto-filled)' },
  { name: 'executive_summary', label: 'Executive Summary', description: 'Extracted executive summary from the report (auto-filled)' },
  { name: 'key_findings', label: 'Key Findings', description: 'Extracted key findings from the report (auto-filled)' },
] as const;

export const LETTER_CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'cover_letter', label: 'Cover Letter' },
  { value: 'proposal', label: 'Proposal' },
  { value: 'renewal', label: 'Renewal' },
  { value: 'endorsement', label: 'Endorsement' },
  { value: 'certificate', label: 'Certificate' },
  { value: 'memo', label: 'Memo' },
  { value: 'custom', label: 'Custom' },
] as const;
